import * as React from 'react';
import {cva, type VariantProps} from 'class-variance-authority';

import {cn} from '../lib/utils';
import {Label} from './label';
import {FieldPath, FieldValues, UseFormReturn} from 'react-hook-form';

const inputVariants = cva(
  cn(
    'flex w-full rounded-lg border border-input px-3 py-1',
    'text-sm shadow-xs transition-colors',
    'file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
    'placeholder:text-muted-foreground',
    'focus-visible:outline-hidden focus-visible:border-ring-muted focus-visible:ring-2 focus-visible:ring-border',
    'disabled:cursor-not-allowed disabled:opacity-50',
  ),
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        filled: 'bg-background',
        ghost:
          'border-transparent bg-transparent shadow-none hover:bg-accent focus-visible:bg-background focus-visible:border-input focus-visible:shadow-xs',
      },
      inputSize: {
        default: 'h-10',
        sm: 'h-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      inputSize: 'default',
    },
  },
);

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  error?: boolean;
  endDecoration?: React.ReactNode;
  startDecoration?: React.ReactNode;
}
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      error,
      endDecoration,
      startDecoration,
      variant,
      inputSize,
      ...props
    },
    ref,
  ) => {
    const inputMarkup = (
      <input
        type={type}
        autoComplete="off"
        className={cn(
          inputVariants({variant, inputSize}),
          error &&
            'border-destructive focus-visible:border-destructive focus-visible:ring-destructive/30',
          endDecoration && 'pr-9',
          startDecoration && 'pl-9',
          className,
        )}
        ref={ref}
        {...props}
      />
    );

    if (endDecoration || startDecoration) {
      return (
        <div className="relative w-full group">
          {startDecoration && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors group-focus-within:text-foreground [&:has(+_input:not(:placeholder-shown))]:text-foreground">
              {startDecoration}
            </div>
          )}
          {inputMarkup}
          {endDecoration && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {endDecoration}
            </div>
          )}
        </div>
      );
    }

    return inputMarkup;
  },
);
Input.displayName = 'Input';

interface FormInputProps<
  TForm extends FieldValues,
  TFieldName extends FieldPath<TForm>,
> extends Omit<InputProps, 'id' | 'form' | 'name'> {
  id: TFieldName;
  label: string;
  form: UseFormReturn<TForm>;
}

const FormInput = <
  TForm extends FieldValues,
  TFieldName extends FieldPath<TForm>,
>({
  id,
  form,
  label,
  ...inputProps
}: FormInputProps<TForm, TFieldName>) => {
  const {
    register,
    formState: {errors},
  } = form;

  const error = errors[id];

  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        {...inputProps}
        {...register(id)}
        className={cn(inputProps.className)}
        error={Boolean(error)}
      />
      {error && (
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        <p className="text-destructive text-sm">{String(error.message)}</p>
      )}
    </div>
  );
};
FormInput.displayName = 'FormInput';

export {Input, FormInput, inputVariants};
